
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function SmartThermostatInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Smart Thermostat Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional smart thermostat installation in Fresno, CA. Nest, Ecobee, Honeywell installation with energy monitoring and remote control for Central Valley climate.',
    url: `${siteUrl}/services/smart-home-energy/smart-thermostat-installation`,
  };

  return (
    <>
      <SEOHead
        title="Smart Thermostat Installation Fresno CA | Nest Ecobee Honeywell"
        description="Smart thermostat installation in Fresno, CA. Professional Nest, Ecobee, and Honeywell installation. Save on cooling costs during hot Central Valley summers. Licensed electrician."
        canonical="/services/smart-home-energy/smart-thermostat-installation"
        keywords="smart thermostat installation Fresno, Nest installation, Ecobee Fresno, smart AC control"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Smart Thermostat Installation — Fresno, CA"
        heroSubtitle="Reduce cooling costs during Fresno's hot summers with intelligent climate control"
        heroImage="https://public.readdy.ai/ai/img_res/0a7b1ea7accccc01fc469604304551c7.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Smart%20thermostat%20device%20with%20digital%20display%20showing%20cooling%20mode%20and%20temperature%20settings%20mounted%20on%20modern%20wall%20with%20smartphone%20displaying%20climate%20control%20app%20contemporary%20interior%20no%20people%20clean%20minimalist%20design&width=700&height=600&seq=smartthermo-fresno-01&orientation=portrait"
        fresnoContent="Fresno's scorching summers mean high cooling costs, but smart thermostats can reduce your AC bills by up to 23%. Our professional installations are specifically configured for Central Valley's extreme climate, with automated schedules that pre-cool your home before peak heat, adjust when you're away, and integrate with your smart home system. We serve all Fresno neighborhoods including Woodward Park, Fig Garden, Tower District, and Clovis with expert smart thermostat installations that maximize energy savings while keeping you comfortable year-round."
        features={[
          {
            icon: 'ri-temp-hot-line',
            title: 'Nest Thermostat Installation',
            description: 'Learning thermostat that adapts to your schedule',
          },
          {
            icon: 'ri-home-gear-line',
            title: 'Ecobee Installation',
            description: 'Smart thermostat with room sensors',
          },
          {
            icon: 'ri-dashboard-line',
            title: 'Honeywell Smart Setup',
            description: 'Reliable smart climate control',
          },
          {
            icon: 'ri-smartphone-line',
            title: 'Remote Access Setup',
            description: 'Control temperature from anywhere',
          },
          {
            icon: 'ri-line-chart-line',
            title: 'Energy Monitoring',
            description: 'Track cooling costs and savings',
          },
          {
            icon: 'ri-voice-recognition-line',
            title: 'Voice Control Integration',
            description: 'Works with Alexa, Google, and Siri',
          },
        ]}
        whatsIncluded={[
          'Complete HVAC system compatibility assessment',
          'Professional removal of old thermostat',
          'Smart thermostat installation with proper wiring',
          'C-wire installation if needed for power',
          'Wi-Fi network connection and configuration',
          'Mobile app setup and user account creation',
          'Automated schedule programming for Fresno climate',
          'Voice assistant integration (Alexa, Google, Siri)',
          'Energy monitoring setup and baseline establishment',
          'Complete system testing and user training',
        ]}
        faqs={[
          {
            question: 'How much can I save on cooling costs with a smart thermostat in Fresno?',
            answer:
              "Fresno homeowners typically save 20-23% on cooling costs with smart thermostats—that's $200-$400 annually given our hot summers. Smart thermostats learn when you're home, adjust temperatures automatically, and prevent overcooling. During Fresno's 100°+ summer days, features like pre-cooling during off-peak hours and automatic adjustments when you're away make a significant difference. Most Fresno homeowners see ROI within 2 years through PG&E bill savings alone.",
          },
          {
            question: 'Does my Fresno home need a C-wire for smart thermostat installation?',
            answer:
              "Most smart thermostats (Nest, Ecobee, Honeywell) require a C-wire (common wire) for continuous power. Many Fresno homes built after 1990 have C-wires already installed but not connected. If your home doesn't have a C-wire, we can install one by running a new wire from your HVAC system or using a C-wire adapter. As licensed electricians, we handle all wiring safely and ensure your smart thermostat has reliable power for Fresno's demanding cooling season.",
          },
          {
            question: "Which smart thermostat is best for Fresno's hot climate?",
            answer:
              "For Fresno's extreme heat, we often recommend Ecobee with room sensors—it monitors temperature in multiple rooms and adjusts cooling to keep your entire home comfortable, not just the hallway where the thermostat is located. Nest is excellent for learning your schedule and pre-cooling before you arrive home. Honeywell offers reliable performance at a lower price point. All three integrate with voice assistants and provide energy reports showing your cooling savings during Fresno's long summer.",
          },
          {
            question: 'Can a smart thermostat work with my older HVAC system in Fresno?',
            answer:
              "Most smart thermostats work with HVAC systems installed in the last 20-30 years, including central AC, heat pumps, and gas furnaces common in Fresno homes. During our assessment, we verify compatibility with your specific system. Older systems without a C-wire or with unusual wiring may need minor modifications, which we handle as part of installation. We ensure your smart thermostat works perfectly with your existing HVAC equipment.",
          },
          {
            question: 'Will my smart thermostat work during power outages in Fresno?',
            answer:
              "Smart thermostats require power to function, so they won't work during outages. However, once power is restored, they automatically reconnect and resume their programmed schedules. For Fresno homes concerned about summer power outages (which can occur during heat waves), we can integrate battery backup systems that keep your thermostat running and maintain climate control even when the grid is down.",
          },
          {
            question: 'How long does smart thermostat installation take in Fresno?',
            answer:
              "Smart thermostat installation typically takes 1-2 hours including removal of your old thermostat, wiring verification, installation, Wi-Fi setup, and programming. If a C-wire needs to be installed, add 1-2 hours. We configure your thermostat specifically for Fresno's climate with automated schedules that maximize savings during our hot summers, and we train you on all features before we leave.",
          },
        ]}
        ctaText="Get Your Smart Thermostat Installed"
      />
    </>
  );
}
